import {graneroInstance} from '@apps-common/geocorn-sdk/api';
import {
  deleteCollection,
  getCollection,
  getCollectionPropertyNames,
  getCollections,
  shareCollection,
  unshareCollection,
  updateCollection,
} from '@apps-common/geocorn-sdk/api/collections';
import {DEFAULT_DATASET} from '@apps-common/geocorn-sdk/api/datasets';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {removeEmpty} from './util.js';
import {getHashFromId} from '@app/util/granero';

export const queryKeys = {
  all: ['collections'],
  lists: () => [...queryKeys.all, 'list'],
  list: params => {
    return [
      ...queryKeys.lists(),
      {dataset: DEFAULT_DATASET, ...removeEmpty({...params})},
    ];
  },
  details: () => [...queryKeys.all, 'details'],
  detail: (id, params) => [
    ...queryKeys.all,
    'details',
    id,
    {'_x-lock': true, ...removeEmpty({...params})},
  ],
  propertyNames: id => [...queryKeys.all, 'propertyNames', id],
};

export const useGetInfiniteCollections = (
  {orgId, ...params} = {},
  queryOptions = {}
) => {
  const queryParams = removeEmpty({
    org_id: orgId,
    '_x-lock': true,
    ...params,
  });
  return useInfiniteQuery({
    queryKey: queryKeys.list({...queryParams}),
    queryFn: ({pageParam: nextPageUrl}) =>
      nextPageUrl
        ? graneroInstance.get(nextPageUrl).then(res => res.data)
        : getCollections({
            params: queryParams,
          }),
    getNextPageParam: lastPage => {
      return (lastPage.links ?? []).find(link => link.rel === 'next')?.href;
    },
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    select: data => {
      return {
        ...data,
        collections: data.pages.map(page => page.collections).flat(),
        numberMatched: data.pages[0].numberMatched,
      };
    },
    ...queryOptions,
    enabled: queryOptions.enabled || !!orgId,
  });
};

export const useGetCollectionsCount = (orgId, queryOptions = {}) => {
  return useGetInfiniteCollections(
    {orgId},
    {
      select: data => data.pages[0].numberMatched,
      ...queryOptions,
    }
  );
};

export const useGetCollection = ({collectionId} = {}, queryOptions = {}) => {
  const queryParams = removeEmpty({
    '_x-lock': true,
  });
  return useQuery({
    queryKey: queryKeys.detail(getHashFromId(collectionId), queryParams),
    queryFn: () => getCollection(collectionId, {params: queryParams}),
    ...queryOptions,
  });
};

export const useGetCollectionPropertyNames = (
  {collectionId} = {},
  queryOptions = {}
) => {
  return useQuery({
    queryKey: queryKeys.propertyNames(getHashFromId(collectionId)),
    queryFn: () => getCollectionPropertyNames(collectionId),
    ...queryOptions,
  });
};

export const useUpdateCollection = (queryOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({collectionId, data}) => updateCollection(collectionId, data),
    ...queryOptions,
    onSuccess: (data, args) => {
      queryClient.invalidateQueries({queryKey: queryKeys.lists()});
      queryClient.setQueryData(
        queryKeys.detail(getHashFromId(args.collectionId)),
        () => data
      );
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, args);
      }
    },
  });
};

export const useDeleteCollection = (queryOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({collectionId}) => deleteCollection(collectionId),
    ...queryOptions,
    onSuccess: (data, args) => {
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, args);
      }
      queryClient.invalidateQueries({queryKey: queryKeys.lists()});
    },
  });
};

export const useShareCollection = (queryOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({collectionId, share}) =>
      share ? shareCollection(collectionId) : unshareCollection(collectionId),
    ...queryOptions,
    onSuccess: (data, args) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lists(),
      });
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, args);
      }
    },
  });
};
